import {formValueSelector} from 'redux-form';

import createSelector from 'web-app/util/custom-create-selector';
import {type RootState} from 'web-app/react/redux/root-state';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isNonEmpty} from 'web-app/util/non-empty-array';

import * as Helpers from './helpers';

const childSignIn = (state: RootState) => state.modals.signIn;

const makeFormValueSelector = () =>
    createSelector(
        (_, props: {childId: string}) => props.childId,
        childId => {
            const formName = Helpers.childSignInFormName(childId);
            return formValueSelector(formName);
        },
    );

const makeGetCurrentPickupTime = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'pickupTime'),
        pickupTime => pickupTime,
    );
};
const makeGetStatementsAnswer = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'statements'),
        statements => statements,
    );
};
export const makeGetIsStatementsRequiredForChild = () =>
    createSelector(
        makeChildSignInById(),
        signIn => signIn && hasValue(signIn.statements) && isNonEmpty(signIn.statements),
    );

export const childSignIns = (state: RootState) => state.modals.signIn.childSignIns;

export const makeChildSignInById = () =>
    createSelector(
        childSignIns,
        (_, props: {childId: string}) => props.childId,
        (signIns, childId) => {
            return signIns.get(childId);
        },
    );

const makeGetSelectedPickupId = () => createSelector(makeChildSignInById(), signIn => signIn?.pickupRelationId);

export const makeGetIsPickupInfoRequiredForChild = () =>
    createSelector(makeChildSignInById(), signIn => Boolean(signIn && signIn.requirePickupInfo));

export const makeGetIsPickupRequired = () =>
    createSelector(
        AccountSelectors.getIsEmployee,
        makeGetIsPickupInfoRequiredForChild(),
        (isEmployee, isPickupInfoRequired) => !isEmployee && isPickupInfoRequired,
    );

export const makeIsChildSignedIn = () =>
    createSelector(makeChildSignInById(), signIn => Boolean(signIn && signIn.checkedIn));

export const makeGetCanSignIn = () =>
    createSelector(makePickupFulfilled(), makeStatementsAreFulfilled(), (pickupFulfilled, statementsAreFulfilled) => {
        return pickupFulfilled && statementsAreFulfilled;
    });

export const makeGetShouldShowPickupInfo = () =>
    createSelector(
        makeIsChildSignedIn(),
        makeGetIsPickupInfoRequiredForChild(),
        (isChildSignedIn, isPickupInfoRequiredForChild) => {
            return !isChildSignedIn && isPickupInfoRequiredForChild;
        },
    );

export const makeGetChildName = () => createSelector(makeChildSignInById(), signIn => signIn?.name ?? '');

export const getHasLocation = createSelector(childSignIn, signIn => signIn.hasLocation);

export const getDistance = createSelector(childSignIn, signIn => signIn.distance);

export const institutionId = createSelector(childSignIns, signIns => {
    const signIn = signIns.first();
    return signIn && signIn.institutionId;
});

export const makeSelectedStatementIds = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'statements'),
        statements => {
            if (hasValue(statements)) {
                return Object.keys(statements);
            }
            return null;
        },
    );
};

export const makePickupFulfilled = () =>
    createSelector(
        makeGetIsPickupRequired(),
        makeGetSelectedPickupId(),
        makeGetCurrentPickupTime(),
        (isPickupRequired, pickupId, pickupTime) => {
            if (isPickupRequired) {
                return hasValue(pickupId) && hasValue(pickupTime);
            }
            return true;
        },
    );

export const makeStatementsAreFulfilled = () =>
    createSelector(
        makeGetIsStatementsRequiredForChild(),
        makeChildSignInById(),
        makeGetStatementsAnswer(),
        (statementsAreRequired, signIn, statementAnswers) => {
            if (statementsAreRequired && hasValue(signIn) && hasValue(signIn.statements)) {
                return (
                    hasValue(statementAnswers) &&
                    signIn.statements.every(statement => statementAnswers[statement.statementId])
                );
            }
            return true;
        },
    );
